import React, { useState, useEffect } from 'react';
import { Bell, Trash2, Settings, ExternalLink, Plus } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import RealTimeStatusBadge from '../../../components/ui/RealTimeStatusBadge';

const SubscriptionManagement = ({ userId }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (userId) {
      fetchSubscriptions();
    }
  }, [userId]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('outage_subscriptions')
        ?.select(`
          id,
          email_enabled,
          sms_enabled,
          created_at,
          service_providers (
            id,
            name,
            category,
            status,
            logo_url
          )
        `)
        ?.eq('user_id', userId);

      if (error) throw error;
      setSubscriptions(data || []);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (subscriptionId) => {
    setActionLoading(prev => ({ ...prev, [subscriptionId]: true }));
    try {
      const { error } = await supabase
        ?.from('outage_subscriptions')
        ?.delete()
        ?.eq('id', subscriptionId);

      if (error) throw error;
      
      setSubscriptions(prev => prev?.filter(sub => sub?.id !== subscriptionId));
    } catch (err) {
      console.error('Error unsubscribing:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [subscriptionId]: false }));
    }
  };

  const handleUpdateSettings = async (subscriptionId, updates) => {
    setActionLoading(prev => ({ ...prev, [subscriptionId]: true }));
    try {
      const { error } = await supabase
        ?.from('outage_subscriptions')
        ?.update(updates)
        ?.eq('id', subscriptionId);

      if (error) throw error;

      setSubscriptions(prev => 
        prev?.map(sub => 
          sub?.id === subscriptionId 
            ? { ...sub, ...updates }
            : sub
        )
      );
    } catch (err) {
      console.error('Error updating subscription settings:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [subscriptionId]: false }));
    }
  };

  const SubscriptionCard = ({ subscription }) => {
    const provider = subscription?.service_providers;
    const isLoading = actionLoading?.[subscription?.id];

    return (
      <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4 flex-1">
            {provider?.logo_url && (
              <img
                src={provider?.logo_url}
                alt={`${provider?.name} logo`}
                className="h-12 w-12 rounded-lg object-contain bg-gray-100 p-2"
              />
            )}
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="font-medium text-gray-900">{provider?.name}</h3>
                <RealTimeStatusBadge status={provider?.status} />
              </div>
              
              <p className="text-sm text-gray-600 capitalize mb-2">
                {provider?.category?.replace('_', ' ')}
              </p>
              
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`email-${subscription?.id}`}
                    checked={subscription?.email_enabled}
                    onChange={(e) => handleUpdateSettings(subscription?.id, { email_enabled: e?.target?.checked })}
                    disabled={isLoading}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor={`email-${subscription?.id}`} className="ml-2 text-gray-700">
                    Email
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`sms-${subscription?.id}`}
                    checked={subscription?.sms_enabled}
                    onChange={(e) => handleUpdateSettings(subscription?.id, { sms_enabled: e?.target?.checked })}
                    disabled={isLoading}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor={`sms-${subscription?.id}`} className="ml-2 text-gray-700">
                    SMS
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 ml-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = `/service-provider-profile?id=${provider?.id}`}
              className="flex items-center"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              View
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleUnsubscribe(subscription?.id)}
              disabled={isLoading}
              className="flex items-center text-red-600 hover:text-red-700 hover:border-red-300"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-1"></div>
              ) : (
                <Trash2 className="h-4 w-4 mr-1" />
              )}
              Unsubscribe
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="border-b border-gray-200 pb-4 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Bell className="h-5 w-5 mr-2" />
            Subscription Management
          </h2>
        </div>
        <div className="space-y-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="animate-pulse border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 bg-gray-300 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="border-b border-gray-200 pb-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Bell className="h-5 w-5 mr-2" />
              Subscription Management
            </h2>
            <p className="text-gray-600 mt-1">
              Manage your service provider subscriptions and notification preferences
            </p>
          </div>
          
          <Button
            variant="outline"
            onClick={() => window.location.href = '/outage-dashboard'}
            className="flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Find Providers
          </Button>
        </div>
      </div>

      {subscriptions?.length > 0 ? (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Bell className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-blue-900">Notification Settings</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Use the checkboxes next to each provider to customize how you receive notifications.
                  Email notifications are instant, while SMS may have carrier delays.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {subscriptions?.map((subscription) => (
              <SubscriptionCard
                key={subscription?.id}
                subscription={subscription}
              />
            ))}
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Subscribed to {subscriptions?.length} service provider{subscriptions?.length !== 1 ? 's' : ''}
            </p>
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Subscriptions Yet
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            You haven't subscribed to any service providers yet. Browse our provider directory
            to start receiving outage notifications.
          </p>
          <Button
            onClick={() => window.location.href = '/outage-dashboard'}
            className="flex items-center mx-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Browse Providers
          </Button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;